/**
 * PromtrManager.ts - Simple prompts and notifications manager
 * 
 * CHANGE HISTORY:
 * - 251102_1633 danielbeer: Added Profylr and Interfacr integration
 * - 251025_1740 danielbeer: Added Actr integration with Actions-Only pattern
 * - 251025_1235 [pending] danielbeer: Initial creation
 * 
 * Manages all simple prompts, alerts, toasts, and dialogs
 * 
 * ARCHITECTURE NOTE: Promptr uses ACTIONS-ONLY pattern
 * 
 * Why Actions-Only:
 * 1. Ephemeral UI: Dialogs and toasts are temporary UI elements
 * 2. No Persistence: Dialog state is not stored
 * 3. Runtime Operations: Show/hide operations are transient
 * 
 * Pattern: Actions-Only
 * - Actions: For dialog operations (show, close, alert, confirm, toast)
 * - No CRUD: Dialog state is ephemeral
 * 
 * INTERFACR INTEGRATION:
 * - Promptr is a UI handler that Interfacr routes to
 * - Receives commands from Interfacr with profile context
 * - Applies Profylr perspective (tone, style, language)
 * 
 * PROFYLR INTEGRATION:
 * - Adapts dialog tone based on profile (professional vs casual)
 * - Adjusts language formality
 * - Respects audience preferences
 */

import {
  ConfirmConfig,
  AlertConfig,
  PromptConfig,
  ToastConfig,
  SelectConfig,
  DrawerConfig,
  ContextMenuConfig,
  PromtrOptions
} from './types';

/**
 * PromtrManager - Simple prompts and notifications
 * 
 * Handles:
 * - Confirm dialogs
 * - Alert messages
 * - Toast notifications
 * - Quick inputs
 * - Select dropdowns
 * - Drawers/sidebars
 * - Context menus
 */
export class PromtrManager {
  private options: PromtrOptions;
  private activeToasts: Set<string> = new Set();
  private actr: any; // Actr instance (optional)
  private interfacr: any; // Interfacr instance (optional)
  private profylr: any; // Profylr instance (optional)

  constructor(options: PromtrOptions = {}) {
    this.options = {
      theme: 'auto',
      defaultDuration: 3000,
      defaultPosition: 'top-right',
      animations: true,
      ...options
    };
    
    this.actr = options.actr;
    this.interfacr = options.interfacr;
    this.profylr = options.profylr;
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
    
    // Register with Interfacr as dialog handler if available
    if (this.interfacr) {
      this.registerWithInterfacr();
    }
  }
  
  /**
   * Register Promptr actions with Actr
   * 
   * ARCHITECTURE NOTE: Promptr uses ACTIONS-ONLY pattern
   * 
   * Why Actions-Only:
   * 1. Ephemeral UI: Dialogs are temporary UI elements
   * 2. No Persistence: Dialog state not stored
   * 3. Runtime Operations: Show/hide operations are transient
   * 
   * Pattern: Actions-Only
   * - Actions: For dialog operations (show, close, alert, confirm)
   * - No CRUD: Dialog state is ephemeral
   */
  private registerActions(): void {
    if (!this.actr) return;
    
    // confirm action
    this.actr.registerAction('promptr:confirm', async (params: ConfirmConfig) => {
      return await this.confirm(params);
    });

    // alert action
    this.actr.registerAction('promptr:alert', async (params: AlertConfig) => {
      return await this.alert(params);
    });

    // prompt action
    this.actr.registerAction('promptr:prompt', async (params: PromptConfig) => {
      return await this.prompt(params);
    });

    // toast action
    this.actr.registerAction('promptr:toast', async (params: { message: string; config?: ToastConfig }) => {
      return this.toast(params.message, params.config);
    });

    // dismissToast action
    this.actr.registerAction('promptr:dismiss-toast', async (params: { id: string }) => {
      return this.dismissToast(params.id);
    });

    // success action
    this.actr.registerAction('promptr:success', async (params: { message: string; duration?: number }) => {
      return this.success(params.message, params.duration);
    });

    // error action
    this.actr.registerAction('promptr:error', async (params: { message: string; duration?: number }) => {
      return this.error(params.message, params.duration);
    });

    // warning action
    this.actr.registerAction('promptr:warning', async (params: { message: string; duration?: number }) => {
      return this.warning(params.message, params.duration);
    });

    // info action
    this.actr.registerAction('promptr:info', async (params: { message: string; duration?: number }) => {
      return this.info(params.message, params.duration);
    });
  }

  /**
   * Register Promptr as Interfacr dialog handler
   */
  private registerWithInterfacr(): void {
    if (!this.interfacr) return;
    
    // Register as handler for dialog operations
    this.interfacr.registerHandler('dialog', {
      confirm: (params: any) => this.confirm(params),
      alert: (params: any) => this.alert(params),
      prompt: (params: any) => this.prompt(params),
      toast: (params: any) => this.toast(params.message, params.config),
    });
  }
  
  /**
   * Apply profile perspective to dialog config
   * Adapts tone, style, and language based on active profile
   */
  private async applyProfilePerspective(config: any): Promise<any> {
    if (!this.profylr) return config;
    
    try {
      const profile = await this.profylr.getActiveProfile();
      if (!profile?.perspective) return config;
      
      const { tone, style, language } = profile.perspective;
      
      // Apply tone adjustments
      if (tone === 'professional' || tone === 'formal') {
        // More formal language
        config.confirmText = config.confirmText || 'Confirm';
        config.cancelText = config.cancelText || 'Cancel';
      } else if (tone === 'casual' || tone === 'friendly') {
        // More casual language
        config.confirmText = config.confirmText || 'Yes';
        config.cancelText = config.cancelText || 'No';
      }
      
      // Apply language preferences
      if (language && language !== 'en-US') {
        // TODO: Integrate with translation system
      }
      
      return config;
    } catch (error) {
      console.warn('Failed to apply profile perspective:', error);
      return config;
    }
  }

  /**
   * Show confirmation dialog
   * 
   * @param config - Confirmation configuration
   * @returns Promise<boolean> - true if confirmed, false if cancelled
   */
  async confirm(config: ConfirmConfig): Promise<boolean> {
    // Apply profile perspective
    const enhancedConfig = await this.applyProfilePerspective(config);
    
    // TODO: Implement with Skinnr-themed modal
    console.log('Promptr.confirm:', enhancedConfig);
    return new Promise((resolve) => {
      // Placeholder implementation
      const confirmed = window.confirm(`${enhancedConfig.title}\n\n${enhancedConfig.message}`);
      resolve(confirmed);
    });
  }

  /**
   * Show alert dialog
   * 
   * @param config - Alert configuration
   */
  async alert(config: AlertConfig): Promise<void> {
    // Apply profile perspective
    const enhancedConfig = await this.applyProfilePerspective(config);
    
    // TODO: Implement with Skinnr-themed modal
    console.log('Promptr.alert:', enhancedConfig);
    return new Promise((resolve) => {
      // Placeholder implementation
      window.alert(`${enhancedConfig.title}\n\n${enhancedConfig.message}`);
      resolve();
    });
  }

  /**
   * Show prompt dialog for text input
   * 
   * @param config - Prompt configuration
   * @returns Promise<string | null> - Input value or null if cancelled
   */
  async prompt(config: PromptConfig): Promise<string | null> {
    // Apply profile perspective
    const enhancedConfig = await this.applyProfilePerspective(config);
    
    // TODO: Implement with Skinnr-themed modal
    console.log('Promptr.prompt:', enhancedConfig);
    return new Promise((resolve) => {
      // Placeholder implementation
      const value = window.prompt(
        `${enhancedConfig.title}\n\n${enhancedConfig.message}`,
        enhancedConfig.defaultValue
      );
      resolve(value);
    });
  }

  /**
   * Show toast notification
   * 
   * @param message - Toast message
   * @param config - Toast configuration
   * @returns Toast ID for dismissal
   */
  toast(message: string, config: Partial<ToastConfig> = {}): string {
    const toastId = `toast-${Date.now()}-${Math.random()}`;
    const toastConfig: ToastConfig = {
      message,
      type: config.type || 'info',
      duration: config.duration ?? this.options.defaultDuration,
      position: config.position || this.options.defaultPosition,
      action: config.action
    };

    // TODO: Implement with Skinnr-themed toast
    console.log('Promptr.toast:', toastConfig);

    this.activeToasts.add(toastId);

    // Auto-dismiss after duration
    if (toastConfig.duration && toastConfig.duration > 0) {
      setTimeout(() => {
        this.dismissToast(toastId);
      }, toastConfig.duration);
    }

    return toastId;
  }

  /**
   * Dismiss a toast notification
   * 
   * @param toastId - Toast ID to dismiss
   */
  dismissToast(toastId: string): void {
    this.activeToasts.delete(toastId);
    // TODO: Implement actual toast dismissal
    console.log('Promptr.dismissToast:', toastId);
  }

  /**
   * Dismiss all active toasts
   */
  dismissAllToasts(): void {
    this.activeToasts.clear();
    // TODO: Implement actual toast dismissal
    console.log('Promptr.dismissAllToasts');
  }

  /**
   * Show select dialog
   * 
   * @param config - Select configuration
   * @returns Promise<T | null> - Selected value or null if cancelled
   */
  async select<T = any>(config: SelectConfig<T>): Promise<T | null> {
    // TODO: Implement with Skinnr-themed select modal
    console.log('Promptr.select:', config);
    return null;
  }

  /**
   * Show drawer/sidebar
   * 
   * @param config - Drawer configuration
   * @returns Promise<void> - Resolves when drawer is closed
   */
  async drawer(config: DrawerConfig): Promise<void> {
    // TODO: Implement with Skinnr-themed drawer
    console.log('Promptr.drawer:', config);
  }

  /**
   * Show context menu
   * 
   * @param config - Context menu configuration
   * @returns Promise<void> - Resolves when menu is closed
   */
  async contextMenu(config: ContextMenuConfig): Promise<void> {
    // TODO: Implement with Skinnr-themed context menu
    console.log('Promptr.contextMenu:', config);
  }

  /**
   * Success toast shorthand
   */
  success(message: string, duration?: number): string {
    return this.toast(message, { type: 'success', duration });
  }

  /**
   * Error toast shorthand
   */
  error(message: string, duration?: number): string {
    return this.toast(message, { type: 'error', duration });
  }

  /**
   * Warning toast shorthand
   */
  warning(message: string, duration?: number): string {
    return this.toast(message, { type: 'warning', duration });
  }

  /**
   * Info toast shorthand
   */
  info(message: string, duration?: number): string {
    return this.toast(message, { type: 'info', duration });
  }
}
